import { Serializable } from '../../common/types'
import { NetworkEdge } from './network_edge.model'
import { NetworkNode, NetworkNodeType } from './network_node.model'
import { FlightNetworkState, FlightNetworkOperatorState } from 'src/app/@store'
import { Center } from 'src/app/centers'

export class FlightNetwork extends Serializable<FlightNetwork> {
  id: number = null
  name: string = null
  operator_id: number = null
  centers: Center[] = []
  edges: NetworkEdge[] = []
  operator: FlightNetworkOperatorState = null
  version: number = null
  safe_up_land_timeout_sec: number = null

  nodesMap = new Map<string, NetworkNode>()
  idMap = new Map<number, NetworkNode>()
  nodesByType = this.emptyNodesByType

  constructor(details: Partial<FlightNetwork> | Partial<FlightNetworkState>) {
    super()
    this.checkAndDeserialize(details)
  }

  get emptyNodesByType(): Map<string, Set<NetworkNode>> {
    return new Map<string, Set<NetworkNode>>(
      Object.keys(NetworkNodeType).map((nodeTypeName) => [nodeTypeName.toLowerCase(), new Set()]),
    )
  }

  deserialize(details: FlightNetwork | FlightNetworkState): void {
    this.id = details.id
    this.name = details.name
    this.operator_id = details.operator_id
    this.operator = details.operator
    this.version = details.version
    this.safe_up_land_timeout_sec = details.safe_up_land_timeout_sec

    this.centers = (details.centers ?? []).map((item: any) => new Center(item))

    if ('nodes' in details) {
      this.nodesMap = new Map()
      this.idMap = new Map()
      this.nodesByType = this.emptyNodesByType
      for (const node of details.nodes ?? []) {
        const newNode = new NetworkNode(node)
        this.nodesMap.set(newNode.idForMap, newNode)
        this.idMap.set(newNode.id, newNode)
        this.nodesByType.get(newNode.node_type.toLowerCase()).add(newNode)
      }
    } else {
      this.nodesMap = details.nodesMap ?? new Map<string, NetworkNode>()
      this.idMap = details.idMap ?? new Map<number, NetworkNode>()
      this.nodesByType = details.nodesByType ?? this.emptyNodesByType
    }

    this.edges = (details.edges ?? []).map((edge) => {
      const newEdge = new NetworkEdge(edge)
      if (!newEdge.fromNodeIdForMap || !newEdge.toNodeIdForMap) {
        newEdge.addNodeIdsForMap(this)
      }
      return newEdge
    })
  }

  toJSON(): FlightNetworkState {
    return {
      id: this.id,
      name: this.name,
      operator_id: this.operator_id,
      operator: this.operator,
      centers: this.centers.map((center) => center.toJSON()),
      nodes: [...this.nodesMap.values()].map((node) => node.toJSON()),
      edges: this.edges.map((edge) => edge.toJSON()),
      version: this.version,
      safe_up_land_timeout_sec: this.safe_up_land_timeout_sec,
    }
  }

  addNode(newNode: NetworkNode): void {
    this.nodesMap.set(newNode.idForMap, newNode)
    this.idMap.set(newNode.id, newNode)
    this.nodesByType.get(newNode.node_type).add(newNode)
  }

  removeNode = (nodeToRemove: NetworkNode): void => {
    this.nodesMap.delete(nodeToRemove.idForMap)
    this.idMap.delete(nodeToRemove.id)
    this.nodesByType.get(nodeToRemove.node_type).delete(nodeToRemove)
  }
}
